import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
} from 'react'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel, {
  FormControlLabelProps,
} from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import styled from 'styled-components'
import TypographyAtom from '../../to_content/typography/typography.atom'
import SvgIconAtom from '../../to_content/svg_icon/svg_icon.atom'
import StackAtom from '../../to_layout/stack/stack.atom'
import button_calenStyles from './button_calendar.styles'
import {dateChoiceType, dateSelected} from '../../../props_reusable'

interface ButtonCalenderSlotHandle {
  setCalender: (arg: string) => void
}

// source types as input at field
type CalenObj = {
  ap_type: dateChoiceType
  ap_day_short: string
  ap_day: number
  ap_date: string
  ap_timeslot?: {[key: string]: string}[]
}

// attribute type as input at field
export interface ButtonCalendarAtomProps {
  ap_calen_arr: CalenObj[]
  setCalenderDate?: (arg: string) => void
}

// picked src type on select the calender
interface CalenLabelAtomProps {
  eachobj: CalenObj
}

// FormControlLabelAtom props
interface FormControlLabelAtomProps extends FormControlLabelProps {
  ap_type: dateChoiceType
  ap_date_selected: dateSelected
}

// atom to wrap the label for styles
const StyleWrapperAtom = styled.div`
  ${button_calenStyles}
`

const FormControlLabelAtom = function (props: FormControlLabelAtomProps) {
  return <FormControlLabel {...props} />
}

// label atom on calender display
const CalenLabelAtom = function (props: CalenLabelAtomProps) {
  const {eachobj} = props
  return (
    <StackAtom ap_type="column">
      <div>
        <TypographyAtom ahd_type="calDate">
          {eachobj.ap_day_short}
        </TypographyAtom>
      </div>
      <div>
        <TypographyAtom ahd_type="calDate">{eachobj.ap_day}</TypographyAtom>
      </div>
      <div>
        {eachobj.ap_type === 'echo' && <SvgIconAtom ap_type="greenLeaf" />}
      </div>
    </StackAtom>
  )
}

// DEFAULT MODULE:
//= ======================================
const ButtonCalendarAtom: ForwardRefRenderFunction<
  ButtonCalenderSlotHandle,
  ButtonCalendarAtomProps
> = function (props, ref) {
  const {ap_calen_arr, setCalenderDate} = props
  const [value, setValue] = React.useState('')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value)
    if (setCalenderDate)
      setCalenderDate((event.target as HTMLInputElement).value)
  }

  const availableDateList = () =>
    ap_calen_arr.filter(each => each.ap_type !== 'disabled')

  useEffect(() => {
    const dateList = availableDateList()
    if (!dateList.length) return
    if (value === '') setValue(dateList[0].ap_date)
    if (setCalenderDate) setCalenderDate(dateList[0].ap_date)
    // eslint-disable-next-line
  }, [])

  const setSlotData = (d: string) => {
    setValue(d)
    if (setCalenderDate) setCalenderDate(d)
  }

  useImperativeHandle(ref, () => ({
    setCalender: setSlotData,
  }))

  return (
    <StyleWrapperAtom>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="calender"
          name="controlled-radio-buttons-group"
          value={value}
          onChange={handleChange}
        >
          <div className="wrapper">
            {ap_calen_arr.map(dateSlot => {
              return (
                <FormControlLabelAtom
                  key={dateSlot.ap_day_short}
                  disabled={dateSlot.ap_type === 'disabled'}
                  ap_date_selected={
                    value === dateSlot.ap_date ? 'true' : 'false'
                  }
                  ap_type={dateSlot.ap_type}
                  value={dateSlot.ap_date}
                  control={<Radio />}
                  label={<CalenLabelAtom eachobj={dateSlot} />}
                />
              )
            })}
          </div>
        </RadioGroup>
      </FormControl>
    </StyleWrapperAtom>
  )
}

// DEFAULT MODULE: EXPORT
//= ======================================
export default forwardRef(ButtonCalendarAtom)
